import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n";

export function AuthForm({ mode }) {
  const { t, lang } = useLang();
  const it = lang === "it";
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", name: "", profession: "", city: "", country: "", income_range: "", phone: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const payload = mode === "login" ? { email: form.email, password: form.password } : form;
      const { data } = await api.post(`/auth/${mode}`, payload);
      setUser(data);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16">
      <h1 className="font-heading text-3xl font-extrabold tracking-tight text-white mb-8">
        {mode === "login" ? t("login") : t("register")}
      </h1>
      <form onSubmit={submit} className="space-y-4">
        {mode === "register" && (
          <input data-testid="auth-name-input" type="text" placeholder={t("name")} value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full bg-[#111111] border border-white/15 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-emerald-500/50" />
        )}
        <input data-testid="auth-email-input" type="email" required placeholder={t("email")} value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          className="w-full bg-[#111111] border border-white/15 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-emerald-500/50" />
        <input data-testid="auth-password-input" type="password" required minLength={8} placeholder={t("password")} value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
          className="w-full bg-[#111111] border border-white/15 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-emerald-500/50" />
        {mode === "register" && (
          <div data-testid="register-profile-section" className="border border-white/10 bg-[#0d0d0d] p-4 space-y-3">
            <p className="text-xs uppercase tracking-widest text-slate-500">
              {it ? "Profilo investitore — facoltativo" : "Investor profile — optional"}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="auth-profession-input" type="text" placeholder={it ? "Attività / Professione" : "Occupation / Profession"} value={form.profession}
                onChange={(e) => setForm({ ...form, profession: e.target.value })}
                className="col-span-2 bg-[#111111] border border-white/15 px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-emerald-500/50" />
              <input data-testid="auth-city-input" type="text" placeholder={it ? "Città di residenza" : "City of residence"} value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                className="bg-[#111111] border border-white/15 px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-emerald-500/50" />
              <input data-testid="auth-country-input" type="text" placeholder={it ? "Paese" : "Country"} value={form.country}
                onChange={(e) => setForm({ ...form, country: e.target.value })}
                className="bg-[#111111] border border-white/15 px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-emerald-500/50" />
              <select data-testid="auth-income-select" value={form.income_range}
                onChange={(e) => setForm({ ...form, income_range: e.target.value })}
                className="bg-[#111111] border border-white/15 px-3 py-2.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-emerald-500/50">
                <option value="">{it ? "Fascia di reddito annuo" : "Annual income range"}</option>
                <option value="<25k">{"< 25.000 €"}</option>
                <option value="25-50k">25.000 - 50.000 €</option>
                <option value="50-100k">50.000 - 100.000 €</option>
                <option value=">100k">{"> 100.000 €"}</option>
              </select>
              <input data-testid="auth-phone-input" type="tel" placeholder={it ? "Telefono" : "Phone"} value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="bg-[#111111] border border-white/15 px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-emerald-500/50" />
            </div>
            <p className="text-[11px] text-slate-500">
              {it ? "Questi dati sono facoltativi e servono solo a personalizzare il servizio. Non vengono condivisi con terzi." : "This data is optional and only used to personalise the service. It is never shared with third parties."}
            </p>
          </div>
        )}
        {error && <p data-testid="auth-error" className="text-sm text-red-400">{error}</p>}
        <button data-testid="auth-submit-btn" type="submit" disabled={busy}
          className="w-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-black font-medium py-3 transition-colors">
          {mode === "login" ? t("login") : t("register")}
        </button>
      </form>
      <p className="text-sm text-slate-400 mt-6 text-center">
        {mode === "login" ? t("no_account") : t("have_account")}{" "}
        <Link data-testid="auth-switch-link" to={mode === "login" ? "/register" : "/login"} className="text-emerald-400 hover:underline">
          {mode === "login" ? t("register") : t("login")}
        </Link>
      </p>
    </div>
  );
}

export default function Login() { return <AuthForm mode="login" />; }
export function Register() { return <AuthForm mode="register" />; }
